import React from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  Image,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';

const isHashHref = (href) => typeof href === 'string' && href.startsWith('#');

/** Converte #RRGGBB in rgba per overlay sulle hero image. */
const hexToRgba = (hex, alpha) => {
  if (!hex || typeof hex !== 'string') return `rgba(0,0,0,${alpha})`;
  const h = hex.replace('#', '');
  if (h.length !== 6) return `rgba(0,0,0,${alpha})`;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if ([r, g, b].some((v) => Number.isNaN(v))) {
    return `rgba(0,0,0,${alpha})`;
  }
  return `rgba(${r},${g},${b},${alpha})`;
};

export function StandaloneHero({
  theme,
  eyebrow,
  title,
  subtitle,
  primaryCta,
  secondaryCta,
  heroImage,
}) {
  const { colors } = theme;

  const textColumn = (
    <Stack spacing={6} maxW="4xl">
      {eyebrow ? (
        <Text
          textTransform="uppercase"
          fontSize="sm"
          fontWeight="800"
          letterSpacing="0.08em"
          fontFamily={theme.fontBody}
          color={colors.accent}
        >
          {eyebrow}
        </Text>
      ) : null}
      <Heading
        as="h1"
        fontSize={{ base: '4xl', md: '6xl' }}
        lineHeight="1.05"
        fontFamily={theme.fontHeading}
        color={theme.key === 'magic' ? '#ffffff' : '#ffffff'}
      >
        {title}
      </Heading>
      {subtitle ? (
        <Text
          fontSize={{ base: 'lg', md: 'xl' }}
          maxW="3xl"
          fontFamily={theme.fontBody}
          color={theme.key === 'magic' ? '#dbeafe' : 'rgba(255,255,255,0.95)'}
        >
          {subtitle}
        </Text>
      ) : null}
      <Stack
        direction={{ base: 'column', sm: 'row' }}
        spacing={4}
        flexWrap="wrap"
      >
        {primaryCta ? (
          isHashHref(primaryCta.href) ? (
            <Button
              as="a"
              href={primaryCta.href}
              size="lg"
              bg={colors.accent}
              color={theme.key === 'magic' ? '#0f1021' : '#171717'}
              _hover={{ opacity: 0.9 }}
              fontFamily={theme.fontBody}
              fontWeight="800"
            >
              {primaryCta.label}
            </Button>
          ) : (
            <Button
              as={RouterLink}
              to={primaryCta.href}
              size="lg"
              bg={colors.accent}
              color={theme.key === 'magic' ? '#0f1021' : '#171717'}
              _hover={{ opacity: 0.9 }}
              fontFamily={theme.fontBody}
              fontWeight="800"
            >
              {primaryCta.label}
            </Button>
          )
        ) : null}
        {secondaryCta ? (
          isHashHref(secondaryCta.href) ? (
            <Button
              as="a"
              href={secondaryCta.href}
              size="lg"
              variant="outline"
              borderColor="rgba(255,255,255,0.75)"
              color="#fff"
              _hover={{ bg: 'rgba(255,255,255,0.12)' }}
              fontFamily={theme.fontBody}
              fontWeight="700"
            >
              {secondaryCta.label}
            </Button>
          ) : (
            <Button
              as={RouterLink}
              to={secondaryCta.href}
              size="lg"
              variant="outline"
              borderColor="rgba(255,255,255,0.75)"
              color="#fff"
              _hover={{ bg: 'rgba(255,255,255,0.12)' }}
              fontFamily={theme.fontBody}
              fontWeight="700"
            >
              {secondaryCta.label}
            </Button>
          )
        ) : null}
      </Stack>
    </Stack>
  );

  const hasHeroPhoto = Boolean(heroImage?.src);

  return (
    <Box
      bg={
        hasHeroPhoto
          ? 'transparent'
          : `linear-gradient(140deg, ${colors.heroStart} 0%, ${colors.heroEnd} 100%)`
      }
      color={colors.text}
      py={{ base: hasHeroPhoto ? 16 : 22, md: hasHeroPhoto ? 20 : 32 }}
      px={4}
      minH={{
        base: hasHeroPhoto ? '56vh' : '52vh',
        md: hasHeroPhoto ? 'min(520px, 58vh)' : '46vh',
      }}
      display="flex"
      alignItems="center"
      position="relative"
      overflow="hidden"
    >
      {hasHeroPhoto ? (
        <>
          <Image
            position="absolute"
            inset={0}
            w="100%"
            h="100%"
            minH="100%"
            objectFit="cover"
            objectPosition="center"
            src={heroImage.src}
            alt={heroImage.alt || ''}
            zIndex={0}
          />
          <Box
            position="absolute"
            inset={0}
            zIndex={1}
            bg={`linear-gradient(140deg, ${hexToRgba(colors.heroStart, 0.82)} 0%, ${hexToRgba(colors.heroEnd, 0.78)} 55%, ${hexToRgba(colors.heroEnd, 0.72)} 100%)`}
            pointerEvents="none"
          />
        </>
      ) : null}
      <Box
        position="absolute"
        w="420px"
        h="420px"
        borderRadius="full"
        bg="rgba(255,255,255,0.12)"
        top="-120px"
        right="-120px"
        zIndex={hasHeroPhoto ? 2 : 0}
        pointerEvents="none"
      />
      <Container
        maxW="container.xl"
        position="relative"
        zIndex={3}
        py={{ base: 2, md: 4 }}
      >
        {textColumn}
      </Container>
    </Box>
  );
}

export function StandaloneFeatureGrid({ theme, title, items = [] }) {
  if (!Array.isArray(items) || !items.length) return null;
  return (
    <Box bg={theme.colors.bg} py={{ base: 10, md: 14 }} px={4}>
      <Container maxW="container.xl">
        <Stack spacing={6}>
          {title ? (
            <Heading
              as="h2"
              fontSize={{ base: '2xl', md: '3xl' }}
              fontFamily={theme.fontHeading}
              color={theme.colors.text}
            >
              {title}
            </Heading>
          ) : null}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
            {items.map((item) => (
              <Box
                key={item}
                bg={theme.colors.surface}
                borderRadius="2xl"
                p={6}
                border="1px solid"
                borderColor={theme.colors.border}
                boxShadow="0 10px 24px rgba(0, 0, 0, 0.08)"
                transform={{ base: 'none', md: 'translateY(8px)' }}
                transition="transform 220ms ease, box-shadow 220ms ease"
                _hover={{
                  transform: { base: 'none', md: 'translateY(-4px)' },
                  boxShadow: '0 14px 28px rgba(0, 0, 0, 0.12)',
                }}
              >
                <Text
                  fontFamily={theme.fontBody}
                  color={theme.colors.text}
                  fontSize="md"
                  fontWeight="700"
                >
                  {item}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        </Stack>
      </Container>
    </Box>
  );
}

export function StandaloneFinalCta({
  theme,
  title,
  text,
  primaryCta,
  secondaryCta,
}) {
  if (!title && !text) return null;
  return (
    <Box bg={theme.colors.surface} py={{ base: 12, md: 16 }} px={4}>
      <Container maxW="container.xl">
        <Box
          borderRadius="3xl"
          p={{ base: 6, md: 10 }}
          bg={`linear-gradient(120deg, ${theme.colors.heroStart} 0%, ${theme.colors.heroEnd} 100%)`}
          color="#fff"
        >
          <Stack spacing={4} align={{ base: 'stretch', md: 'center' }}>
            {title ? (
              <Heading
                as="h2"
                fontSize={{ base: '2xl', md: '4xl' }}
                fontFamily={theme.fontHeading}
                textAlign={{ base: 'left', md: 'center' }}
              >
                {title}
              </Heading>
            ) : null}
            {text ? (
              <Text
                maxW="3xl"
                fontFamily={theme.fontBody}
                textAlign={{ base: 'left', md: 'center' }}
                opacity={0.95}
              >
                {text}
              </Text>
            ) : null}
            <Stack direction={{ base: 'column', md: 'row' }} spacing={3}>
              {primaryCta ? (
                <Button
                  as={RouterLink}
                  to={primaryCta.href}
                  bg={theme.colors.accent}
                  color={theme.key === 'magic' ? '#0f1021' : '#1c1917'}
                  fontWeight="800"
                  fontFamily={theme.fontBody}
                  _hover={{ opacity: 0.9 }}
                >
                  {primaryCta.label}
                </Button>
              ) : null}
              {secondaryCta ? (
                <Button
                  as={RouterLink}
                  to={secondaryCta.href}
                  variant="outline"
                  borderColor="rgba(255,255,255,0.75)"
                  color="#fff"
                  fontFamily={theme.fontBody}
                  _hover={{ bg: 'rgba(255,255,255,0.14)' }}
                >
                  {secondaryCta.label}
                </Button>
              ) : null}
            </Stack>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}
